import axios from 'axios';

// Используем текущий origin для API (работает в iframe)
const API_BASE_URL = import.meta.env.VITE_API_URL || window.location.origin;

export interface SettingsData {
  baseUrl?: string;
  authToken?: string;
  customerSearchType?: 'internal' | 'external';
  giftCardSettings?: {
    numberLength?: number;
    defaultExpireDays?: number;
  };
  giftCardProduct?: {
    productName?: string;
  };
}

export const api = {
  async getSettings(accountId?: string): Promise<SettingsData> {
    // Получаем accountId из параметра или автоматически
    const id = accountId || await getAccountId();
    if (!id) {
      throw new Error('Account ID не найден');
    }
    
    const response = await axios.get(`${API_BASE_URL}/api/settings`, {
      params: { accountId: id },
    });
    return response.data;
  },

  async saveSettings(settings: SettingsData): Promise<void> {
    const accountId = await getAccountId();
    if (!accountId) {
      throw new Error('Account ID не найден');
    }
    
    await axios.post(`${API_BASE_URL}/api/settings`, {
      accountId,
      ...settings,
    });
  },

  async saveSettingsWithAccountId(accountId: string, settings: SettingsData): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/settings`, {
      accountId,
      ...settings,
    });
  },
};

// Интерфейсы для сертификатов
export interface GiftCard {
  id: string;
  name: string;
  initialBalance: string;
  currentBalance: string;
  status: 'CAN_BE_USED' | 'PARTIALLY_REDEEMED' | 'EXPIRED' | 'REDEEMED' | 'INVALID';
  expireDate: string | null;
  retailStoreId: string | null;
  createdAt: string;
  updatedAt: string;
  transactionsCount?: number;
}

export interface GiftCardTransaction {
  id: string;
  amount: string;
  type: 'REDEEM' | 'REFUND';
  saleId: string | null;
  createdAt: string;
}

export interface CreateGiftCardData {
  name?: string; // Опционально, будет сгенерирован автоматически если не указан
  initialBalance: number;
  expireDate?: string;
  quantity?: number; // Количество сертификатов для создания (по умолчанию 1)
}

export interface UpdateGiftCardData {
  name?: string;
  expireDate?: string | null;
  status?: string;
  currentBalance?: number;
}

// Интерфейсы для покупателей
export interface Customer {
  id: string;
  msId: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  discountCardNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerData {
  msId?: string;
  name?: string;
  phone?: string;
  email?: string;
  discountCardNumber?: string;
}

export interface UpdateCustomerData {
  msId?: string;
  name?: string;
  phone?: string;
  email?: string;
  discountCardNumber?: string;
}

// API для сертификатов
export const giftCardsApi = {
  async getAll(
    accountId?: string,
    page?: number,
    limit?: number,
    search?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ): Promise<{
    giftCards: GiftCard[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  }> {
    const id = accountId || await getAccountId();
    const response = await axios.get(`${API_BASE_URL}/api/giftcards`, {
      params: { accountId: id, page, limit, search, status, sortBy, sortOrder },
    });
    return response.data;
  },

  async getById(id: string, accountId?: string): Promise<GiftCard & { transactions: GiftCardTransaction[] }> {
    const accId = accountId || await getAccountId();
    const response = await axios.get(`${API_BASE_URL}/api/giftcards/${id}`, {
      params: { accountId: accId },
    });
    return response.data;
  },

  async create(data: CreateGiftCardData, accountId?: string): Promise<GiftCard | { giftCards: GiftCard[]; count: number }> {
    const accId = accountId || await getAccountId();
    const response = await axios.post(`${API_BASE_URL}/api/giftcards`, data, {
      params: { accountId: accId },
    });
    return response.data;
  },

  async update(id: string, data: UpdateGiftCardData, accountId?: string): Promise<GiftCard> {
    const accId = accountId || await getAccountId();
    const response = await axios.put(`${API_BASE_URL}/api/giftcards/${id}`, data, {
      params: { accountId: accId },
    });
    return response.data;
  },

  async delete(id: string, accountId?: string): Promise<void> {
    const accId = accountId || await getAccountId();
    await axios.delete(`${API_BASE_URL}/api/giftcards/${id}`, {
      params: { accountId: accId },
    });
  },

  async getHistory(id: string, accountId?: string): Promise<{
    history: Array<{
      id: string;
      type: 'history' | 'transaction';
      action: string;
      description: string | null;
      oldValue: string | null;
      newValue: string | null;
      saleId: string | null;
      createdAt: string;
    }>;
  }> {
    const accId = accountId || await getAccountId();
    const response = await axios.get(`${API_BASE_URL}/api/giftcards/${id}/history`, {
      params: { accountId: accId },
    });
    return response.data;
  },

  async logDownload(id: string, accountId?: string): Promise<void> {
    const accId = accountId || await getAccountId();
    await axios.post(`${API_BASE_URL}/api/giftcards/${id}/download`, {}, {
      params: { accountId: accId },
    });
  },

  async getAllHistory(filters?: { giftCardName?: string; action?: string }, accountId?: string): Promise<{
    history: Array<{
      id: string;
      giftCardId: string;
      giftCardName: string;
      action: string;
      description: string | null;
      oldValue: string | null;
      newValue: string | null;
      saleId: string | null;
      createdAt: string;
    }>;
  }> {
    const accId = accountId || await getAccountId();
    const response = await axios.get(`${API_BASE_URL}/api/giftcards/history/all`, {
      params: { 
        accountId: accId,
        ...(filters?.giftCardName && { giftCardName: filters.giftCardName }),
        ...(filters?.action && { action: filters.action }),
      },
    });
    return response.data;
  },
};

// API для покупателей
export const customersApi = {
  async getAll(accountId?: string, search?: string, limit?: number, offset?: number): Promise<{
    customers: Customer[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const id = accountId || await getAccountId();
    const response = await axios.get(`${API_BASE_URL}/api/customers`, {
      params: { accountId: id, search, limit, offset },
    });
    return response.data;
  },

  async getById(id: string, accountId?: string): Promise<Customer> {
    const accId = accountId || await getAccountId();
    const response = await axios.get(`${API_BASE_URL}/api/customers/${id}`, {
      params: { accountId: accId },
    });
    return response.data;
  },

  async create(data: CreateCustomerData, accountId?: string): Promise<Customer> {
    const accId = accountId || await getAccountId();
    const response = await axios.post(`${API_BASE_URL}/api/customers`, data, {
      params: { accountId: accId },
    });
    return response.data;
  },

  async update(id: string, data: UpdateCustomerData, accountId?: string): Promise<Customer> {
    const accId = accountId || await getAccountId();
    const response = await axios.put(`${API_BASE_URL}/api/customers/${id}`, data, {
      params: { accountId: accId },
    });
    return response.data;
  },

  async delete(id: string, accountId?: string): Promise<void> {
    const accId = accountId || await getAccountId();
    await axios.delete(`${API_BASE_URL}/api/customers/${id}`, {
      params: { accountId: accId },
    });
  },
};

// Кеш для accountId в памяти (избегаем повторных запросов)
let accountIdCache: string | null = null;
let accountIdPromise: Promise<string> | null = null;
let cachedContextKey: string | null = null; // Кеш для contextKey для проверки смены аккаунта

export async function getAccountId(): Promise<string> {
  const urlParams = new URLSearchParams(window.location.search);
  const contextKey = urlParams.get('contextKey');
  
  // Если contextKey изменился, очищаем кеш и localStorage (смена аккаунта)
  if (cachedContextKey && cachedContextKey !== contextKey) {
    console.log('ContextKey changed, clearing cache and localStorage');
    accountIdCache = null;
    localStorage.removeItem('accountId'); // Очищаем localStorage при смене аккаунта
    cachedContextKey = null;
  }
  
  // Если уже есть в кеше и contextKey совпадает, возвращаем сразу
  if (accountIdCache && cachedContextKey === contextKey) {
    return accountIdCache;
  }

  // Если запрос уже выполняется, возвращаем тот же промис
  if (accountIdPromise) {
    return accountIdPromise;
  }

  // Создаём новый промис для получения accountId
  accountIdPromise = (async () => {
    const appUid = urlParams.get('appUid');
    
    // ВСЕГДА получаем accountId из contextKey через API (не используем localStorage без проверки)
    if (contextKey) {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/context/${contextKey}`,
          {},
          { params: appUid ? { appUid } : {} }
        );
        if (response.data.accountId) {
          // Сохраняем только после успешного получения из API
          localStorage.setItem('accountId', response.data.accountId);
          accountIdCache = response.data.accountId;
          cachedContextKey = contextKey; // Сохраняем contextKey для проверки
          accountIdPromise = null;
          return response.data.accountId;
        }
      } catch (e) {
        console.error('Failed to get accountId from contextKey:', e);
        // При ошибке очищаем localStorage
        localStorage.removeItem('accountId');
      }
    }

    accountIdPromise = null;
    console.warn('AccountId not found');
    return '';
  })();

  return accountIdPromise;
}

